import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActionIcon } from '@mantine/core';
import { Activity, BarChart3, ListChecks, PieChart, Sparkles, TrendingUp, X, type LucideIcon } from 'lucide-react';
import FeatureNavLayout from '@/shared/surfaces/FeatureNavLayout';
import type { FeatureNavGroup, FeatureNavItem } from '@/shared/surfaces/FeatureNavTypes';
import { useSurface } from '@/shared/surfaces/useSurface';
import { usePinnedTilesStore } from '@/shared/surfaces/usePinnedTiles';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { BEHAVIOR_CATALOG } from './BehaviorTopicCatalog';

const VIZ_ICONS: Record<string, LucideIcon> = {
    'ranked-bars': BarChart3,
    donut: PieChart,
    trend: TrendingUp,
};

/**
 * The Behavior surface shell: a full-height interior sub-nav column (the
 * analytics views in catalog order, use cases first, any pinned "Custom" tiles,
 * then the Activity turn feed below a divider) beside its own scrollable content
 * area. Drill-ins (topic → people → prompts) render inside the outlet so the
 * rail keeps the current view highlighted.
 */
export default function BehaviorLayout(): ReactElement {
    const surface = useSurface('behavior');
    const store = usePinnedTilesStore();
    const location = useLocation();
    const navigate = useNavigate();

    if (surface === undefined) return <TopicNotFound />;

    const toNavItem = (topic: (typeof BEHAVIOR_CATALOG)[number]): FeatureNavItem => ({
        path: `${surface.basePath}/${topic.id}`,
        label: topic.navLabel ?? topic.title,
        icon: topic.icon,
    });
    const items: FeatureNavItem[] = [{ path: `${surface.basePath}/use-cases`, label: 'Use Cases', icon: ListChecks }, ...BEHAVIOR_CATALOG.map(toNavItem)];
    const activityItem: FeatureNavItem = { path: `${surface.basePath}/activity`, label: 'Activity', icon: Activity };

    const handleUnpin = (id: string, path: string): void => {
        store.remove(id);
        if (location.pathname === path || location.pathname.startsWith(`${path}/`)) {
            navigate(items[0]?.path ?? surface.basePath);
        }
    };

    const pinnedItems: FeatureNavItem[] = store.tilesFor(surface.id).map((tile) => {
        const path = `${surface.basePath}/${tile.id}`;
        return {
            path,
            label: tile.spec.title,
            icon: VIZ_ICONS[tile.spec.viz] ?? Sparkles,
            rightSection: (
                <ActionIcon
                    component="div"
                    size="xs"
                    variant="subtle"
                    color="gray"
                    aria-label={`Remove ${tile.spec.title} from the nav`}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleUnpin(tile.id, path);
                    }}
                >
                    <X size={12} />
                </ActionIcon>
            ),
        };
    });

    const groups: FeatureNavGroup[] = [
        { header: { icon: BarChart3, label: 'Usage' }, items, selectGroupLabel: 'Analytics' },
        ...(pinnedItems.length > 0 ? [{ header: { icon: Sparkles, label: 'Custom' }, items: pinnedItems, dividerBefore: true, selectGroupLabel: 'Custom' }] : []),
        { items: [activityItem], dividerBefore: true, selectGroupLabel: 'Activity' },
    ];

    return <FeatureNavLayout title={surface.title} ariaLabel="Behavior view" groups={groups} />;
}
